import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import StatCard from '../../components/ui/StatCard';
import GradientIcon from '../../components/ui/GradientIcon';
import { FileText, PlayCircle, Users, BookOpen } from 'lucide-react';
import GaugeMeter from '../../components/ui/GaugeMeter';
import Pagination from '../../components/ui/Pagination';
import toast from 'react-hot-toast';
import { clientService } from '../../services';
import exportService from '../../services/exportService';
import {
  getMetricBandInfo,
  getTEIInterpretation,
  getLAIInterpretation,
} from '../../utils/reportingConstants';

/**
 * Reports Page (Client)
 * View and export various training reports
 */

const ReportsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedTrainerId = searchParams.get('trainer');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [activeReportView, setActiveReportView] = useState(selectedTrainerId ? 'trainer' : 'session'); // 'session' or 'trainer'
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [stats, setStats] = useState({
    total: 0,
    summary: 0,
    session: 0,
    analytics: 0,
    participant: 0,
    trainer: 0,
  });
  const [newReportData, setNewReportData] = useState({
    type: 'session',
    dateRange: '30d',
    format: 'PDF',
  });
  const [isExporting, setIsExporting] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  // Trainer performance preview modal state
  const [showTrainerPreview, setShowTrainerPreview] = useState(false);
  const [previewTrainer, setPreviewTrainer] = useState(null);
  const [trainerAnalytics, setTrainerAnalytics] = useState(null);
  const [loadingTrainerPreview, setLoadingTrainerPreview] = useState(false);

  useEffect(() => {
    fetchReports();
  }, [filterType]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const response = await clientService.getReports({
        type: filterType,
        search: searchTerm,
      });
      if (response.success) {
        setReports(response.data || []);
        setStats(response.stats || {
          total: 0,
          summary: 0,
          session: 0,
          analytics: 0,
          participant: 0,
          trainer: 0,
        });
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter reports by active view (session or trainer) and search term
  const filteredReports = reports.filter((report) => {
    // First filter by active report view
    // For session view, only show session and participant reports (not summary, analytics, or trainer)
    if (activeReportView === 'session' && !['session', 'participant'].includes(report.type)) return false;
    if (activeReportView === 'trainer' && report.type !== 'trainer') return false;

    // If a specific trainer is selected via URL, filter to only that trainer's report
    if (selectedTrainerId && activeReportView === 'trainer') {
      const reportTrainerId = report.trainerId?.toString() || '';
      if (reportTrainerId !== selectedTrainerId && !report.id?.includes(selectedTrainerId)) {
        return false;
      }
    }

    // Then filter by search term
    if (!searchTerm) return true;
    return (
      report.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  // Pagination
  const totalPages = Math.ceil(filteredReports.length / itemsPerPage);
  const paginatedReports = filteredReports.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterType, activeReportView, itemsPerPage]);

  const handleDownloadReport = async (report) => {
    try {
      // If report has a sessionId, export session analytics report PDF
      if (report.sessionId) {
        await exportService.exportSessionAnalyticsReportPDF(report.sessionId, report.sessionCode);
        toast.success('Report downloaded successfully');
      } else if (report.downloadUrl) {
        // If report has a download URL, open it
        window.open(report.downloadUrl, '_blank');
        toast.success('Report downloaded successfully');
      } else if (report.type === 'summary' || report.type === 'analytics') {
        // For summary/analytics reports, export analytics CSV
        await exportService.exportAnalyticsCSV(30);
        toast.success('Analytics report downloaded successfully');
      } else if (report.type === 'trainer' && report.trainerId) {
        // For trainer reports, export trainer-specific performance report as PDF
        const trainerName = report.title?.replace(' - Trainer Performance', '') || '';
        await exportService.exportTrainerReportPDF(report.trainerId, trainerName);
        toast.success('Trainer report downloaded successfully');
      } else {
        toast.error('Download not available for this report');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error(error.message || 'Failed to download report');
    }
  };

  const handlePreviewReport = async (report) => {
    try {
      if (report.previewUrl) {
        // Open preview URL in new tab
        window.open(report.previewUrl, '_blank');
      } else if (report.sessionId) {
        // Navigate to session detail/report page for preview
        navigate(`/client/sessions/${report.sessionId}`);
      } else if (report.type === 'trainer' && report.trainerId) {
        // Fetch trainer analytics data and show preview modal
        setLoadingTrainerPreview(true);
        setShowTrainerPreview(true);
        setTrainerAnalytics(null);
        try {
          const response = await clientService.getTrainerAnalytics(report.trainerId, { period: '30d' });
          if (response.success) {
            setPreviewTrainer(response.data.trainer);
            setTrainerAnalytics(response.data);
          } else {
            toast.error('Failed to load trainer data');
            setShowTrainerPreview(false);
          }
        } catch (err) {
          console.error('Error fetching trainer analytics:', err);
          toast.error('Failed to load trainer data');
          setShowTrainerPreview(false);
        } finally {
          setLoadingTrainerPreview(false);
        }
      } else if (report.type === 'trainer') {
        // Navigate to trainers list page
        navigate(`/client/trainers`);
      } else if (report.type === 'summary' || report.type === 'analytics') {
        // Navigate to analytics page for summary/analytics reports
        navigate('/client/analytics');
      } else {
        // Show report details in a toast as fallback
        toast.success(`Previewing: ${report.title}`);
      }
    } catch (error) {
      console.error('Preview error:', error);
      toast.error('Failed to preview report');
    }
  };

  const handleShareReport = async (report) => {
    try {
      // Create a shareable link
      const shareUrl = report.shareUrl || `${window.location.origin}/reports/${report.id}`;

      // Try to use the Web Share API if available
      if (navigator.share) {
        await navigator.share({
          title: report.title,
          text: report.description,
          url: shareUrl,
        });
        toast.success('Report shared successfully');
      } else {
        // Fallback: copy to clipboard
        // Check if clipboard API is available (requires HTTPS or localhost)
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(shareUrl);
          toast.success('Link copied to clipboard');
        } else {
          // Final fallback: use textarea method for HTTP contexts
          const textArea = document.createElement('textarea');
          textArea.value = shareUrl;
          textArea.style.position = 'fixed';
          textArea.style.left = '-999999px';
          textArea.style.top = '-999999px';
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          try {
            document.execCommand('copy');
            toast.success('Link copied to clipboard');
          } catch (err) {
            toast.error('Failed to copy link');
          }
          document.body.removeChild(textArea);
        }
      }
    } catch (error) {
      // If user cancelled share or clipboard failed
      if (error.name !== 'AbortError') {
        console.error('Share error:', error);
        toast.error('Failed to share report');
      }
    }
  };

  const handleExportAll = async () => {
    setIsExporting(true);
    try {
      await exportService.exportSessionsCSV();
      toast.success('All sessions exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error(error.message || 'Failed to export');
    } finally {
      setIsExporting(false);
    }
  };

  const handleGenerateReport = () => {
    setShowGenerateModal(true);
  };

  const handleCreateReport = async () => {
    setIsGenerating(true);
    try {
      // Map date range to days
      const periodMap = {
        '7d': 7,
        '30d': 30,
        '90d': 90,
        '365d': 365,
      };
      const period = periodMap[newReportData.dateRange] || 30;

      if (newReportData.format === 'CSV' || newReportData.format === 'Excel') {
        await exportService.exportAnalyticsCSV(period);
      } else {
        // For PDF, export sessions CSV as fallback for now
        await exportService.exportSessionsCSV();
      }

      toast.success(`${newReportData.type} report generated successfully`);
      setShowGenerateModal(false);
      setNewReportData({
        type: 'session',
        dateRange: '30d',
        format: 'PDF',
      });
    } catch (error) {
      console.error('Generate report error:', error);
      toast.error(error.message || 'Failed to generate report');
    } finally {
      setIsGenerating(false);
    }
  };

  const getReportIcon = (type) => {
    switch (type) {
      case 'summary':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case 'session':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        );
      case 'analytics':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        );
      case 'participant':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        );
      case 'trainer':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        );
      default:
        return null;
    }
  };

  const getStatsIcon = (type, className = "w-6 h-6") => {
    switch (type) {
      case 'total':
        return (
          <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case 'summary':
        return (
          <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case 'session':
        return (
          <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        );
      case 'analytics':
        return (
          <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        );
      case 'participant':
        return (
          <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        );
      case 'trainer':
        return (
          <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        );
      default:
        return null;
    }
  };

  const getReportTypeLabel = (type) => {
    const labels = {
      summary: 'Summary',
      session: 'Session',
      analytics: 'Analytics',
      participant: 'Participant',
      trainer: 'Trainer',
    };
    return labels[type] || type;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <DashboardLayout>
      <div className="p-4 space-y-4">
        {/* Back Button */}
        <div className="mb-4">
          <button
            onClick={() => navigate('/client/dashboard')}
            className="inline-flex items-center gap-2 text-neutral-600 hover:text-neutral-900 transition-colors mb-2 group"
          >
            <svg className="w-4 h-4 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="text-sm font-medium">Back to Dashboard</span>
          </button>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-neutral-900">Reports</h1>
            <p className="text-xs sm:text-sm text-neutral-600 mt-1">View and export training reports</p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <Button variant="ghost" size="sm" onClick={handleExportAll} disabled={isExporting}>
              {isExporting ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  <span className="hidden sm:inline">Export All</span>
                </>
              )}
            </Button>
            <Button variant="primary" size="sm" onClick={handleGenerateReport}>
              <svg className="w-4 h-4 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span className="hidden sm:inline">Generate Report</span>
            </Button>
          </div>
        </div>

        {/* Report Type Toggle Buttons */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setActiveReportView('session')}
            className={`px-6 py-2.5 rounded-lg font-medium text-sm transition-all flex items-center gap-2 ${
              activeReportView === 'session'
                ? 'bg-primary-600 text-white shadow-md'
                : 'bg-white text-neutral-700 border border-neutral-200 hover:border-primary-300 hover:bg-primary-50'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            Session Reports
          </button>
          <button
            onClick={() => setActiveReportView('trainer')}
            className={`px-6 py-2.5 rounded-lg font-medium text-sm transition-all flex items-center gap-2 ${
              activeReportView === 'trainer'
                ? 'bg-primary-600 text-white shadow-md'
                : 'bg-white text-neutral-700 border border-neutral-200 hover:border-primary-300 hover:bg-primary-50'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            Trainer Reports
          </button>
        </div>

        {/* Trainer Filter Banner */}
        {selectedTrainerId && activeReportView === 'trainer' && (
          <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <p className="text-sm text-primary-800">
                Showing performance report for selected trainer
              </p>
            </div>
            <button
              onClick={() => navigate('/client/reports')}
              className="text-sm font-medium text-primary-700 hover:text-primary-900 flex items-center gap-1"
            >
              View All Trainers
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total Reports"       value={stats.total}       icon={FileText}    variant="slate" />
          <StatCard label="Session Reports"     value={stats.session}     icon={PlayCircle}  variant="emerald" />
          <StatCard label="Participant Reports" value={stats.participant} icon={Users}       variant="blue" />
          <StatCard label="Trainer Reports"     value={stats.trainer}     icon={BookOpen}    variant="violet" />
        </div>

        {/* Reports Table Card */}
        <div className="bg-white rounded-2xl border border-neutral-100 overflow-hidden">
          {/* Search and Filters inside card */}
          <div className="p-5 border-b border-neutral-100">
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
              <div className="relative flex-1 w-full sm:max-w-md">
                <svg
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search reports..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-10 py-2 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="flex-1 sm:flex-none px-3 py-2 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 bg-white appearance-none pr-8 cursor-pointer"
                  style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.5rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.25em 1.25em' }}
                >
                  <option value="all">All Types</option>
                  <option value="session">Session</option>
                  <option value="participant">Participant</option>
                  <option value="trainer">Trainer</option>
                </select>
              </div>
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <div className="py-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-primary-500 border-t-transparent"></div>
              <p className="mt-3 text-sm text-neutral-500">Loading reports...</p>
            </div>
          ) : filteredReports.length === 0 ? (
            <div className="text-center py-16">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-neutral-100 rounded-full mb-4">
                {activeReportView === 'trainer' ? (
                  <svg className="w-6 h-6 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                )}
              </div>
              <p className="text-sm text-neutral-600 font-medium mb-1">
                No {activeReportView === 'trainer' ? 'trainer' : 'session'} reports found
              </p>
              <p className="text-xs text-neutral-400 mb-4">
                {searchTerm
                  ? 'Try adjusting your search'
                  : activeReportView === 'trainer'
                    ? 'Add trainers to your organization to see trainer performance reports'
                    : 'Complete a session to see session reports'}
              </p>
              {activeReportView === 'trainer' && stats.trainer === 0 && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => navigate('/client/trainers')}
                >
                  Add Trainers
                </Button>
              )}
            </div>
          ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead>
                <tr className="border-b border-neutral-100">
                  <th className="text-left py-3.5 px-5 text-sm font-medium text-neutral-500">Report</th>
                  <th className="text-left py-3.5 px-5 text-sm font-medium text-neutral-500">Type</th>
                  <th className="text-left py-3.5 px-5 text-sm font-medium text-neutral-500">Date</th>
                  <th className="text-left py-3.5 px-5 text-sm font-medium text-neutral-500">Format</th>
                  <th className="text-left py-3.5 px-5 text-sm font-medium text-neutral-500">Generated By</th>
                  <th className="text-left py-3.5 px-5 text-sm font-medium text-neutral-500">Status</th>
                  <th className="text-right py-3.5 px-5 text-sm font-medium text-neutral-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedReports.map((report) => (
                  <tr
                    key={report.id}
                    className="border-b border-neutral-50 hover:bg-neutral-50/60 transition-colors"
                  >
                    <td className="py-4 px-5">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary-50 rounded-full flex items-center justify-center text-primary-600 flex-shrink-0">
                          {getReportIcon(report.type)}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-neutral-900 truncate max-w-[250px]">{report.title}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-5">
                      <span className="text-xs font-medium px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full">
                        {getReportTypeLabel(report.type)}
                      </span>
                    </td>
                    <td className="py-4 px-5">
                      <span className="text-sm text-neutral-700">{formatDate(report.generatedDate)}</span>
                    </td>
                    <td className="py-4 px-5">
                      <span className="text-sm text-neutral-700">{report.format}</span>
                    </td>
                    <td className="py-4 px-5">
                      <span className="text-sm text-neutral-700">{report.generatedBy}</span>
                    </td>
                    <td className="py-4 px-5">
                      <Badge variant="success" size="sm">
                        {report.status}
                      </Badge>
                    </td>
                    <td className="py-4 px-5">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => handleDownloadReport(report)} className="p-2 rounded-lg text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors" title="Download">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                        </button>
                        <button onClick={() => handlePreviewReport(report)} className="p-2 rounded-lg text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors" title="Preview">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                        <button onClick={() => handleShareReport(report)} className="p-2 rounded-lg text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors" title="Share">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}

          {/* Pagination */}
          {!loading && filteredReports.length > 0 && (
            <div className="px-3 sm:px-5 py-3 sm:py-3.5 border-t border-neutral-100">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                itemsPerPage={itemsPerPage}
                totalItems={filteredReports.length}
              />
            </div>
          )}
        </div>

        {/* Generate Report Modal */}
        {showGenerateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-xl shadow-xl max-w-md w-full"
            >
              <div className="p-6 border-b border-neutral-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-neutral-900">Generate New Report</h2>
                  <button
                    onClick={() => setShowGenerateModal(false)}
                    className="text-neutral-400 hover:text-neutral-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="p-6 space-y-4">
                {/* Report Type */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Report Type
                  </label>
                  <select
                    value={newReportData.type}
                    onChange={(e) => setNewReportData({ ...newReportData, type: e.target.value })}
                    className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="session">Session Report</option>
                    <option value="participant">Participant Report</option>
                    <option value="trainer">Trainer Report</option>
                  </select>
                </div>

                {/* Date Range */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Date Range
                  </label>
                  <select
                    value={newReportData.dateRange}
                    onChange={(e) => setNewReportData({ ...newReportData, dateRange: e.target.value })}
                    className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="7d">Last 7 Days</option>
                    <option value="30d">Last 30 Days</option>
                    <option value="90d">Last 90 Days</option>
                    <option value="180d">Last 6 Months</option>
                    <option value="365d">Last Year</option>
                    <option value="custom">Custom Range</option>
                  </select>
                </div>

                {/* Format */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Export Format
                  </label>
                  <select
                    value={newReportData.format}
                    onChange={(e) => setNewReportData({ ...newReportData, format: e.target.value })}
                    className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="PDF">PDF</option>
                    <option value="Excel">Excel</option>
                    <option value="CSV">CSV</option>
                  </select>
                </div>
              </div>
              <div className="p-6 border-t border-neutral-200 flex items-center justify-end gap-3">
                <Button variant="ghost" onClick={() => setShowGenerateModal(false)}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleCreateReport} disabled={isGenerating}>
                  {isGenerating ? (
                    <>
                      <svg className="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Generating...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Generate Report
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Trainer Performance Preview Modal */}
        {showTrainerPreview && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            >
              {loadingTrainerPreview ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                  <p className="text-neutral-600">Loading trainer performance...</p>
                </div>
              ) : previewTrainer && trainerAnalytics ? (
                <>
                  {/* Modal Header */}
                  <div className="p-6 border-b border-neutral-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white text-xl font-bold">
                          {previewTrainer.firstName?.charAt(0)}{previewTrainer.lastName?.charAt(0)}
                        </div>
                        <div>
                          <h2 className="text-xl font-bold text-neutral-900">
                            {previewTrainer.firstName} {previewTrainer.lastName}
                          </h2>
                          <p className="text-sm text-neutral-600">Performance Report - Last 30 Days</p>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setShowTrainerPreview(false);
                          setPreviewTrainer(null);
                          setTrainerAnalytics(null);
                        }}
                        className="text-neutral-400 hover:text-neutral-600"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Performance Content */}
                  <div className="p-6 space-y-6">
                    {/* Key Metrics Row */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-blue-50 rounded-xl p-4 text-center">
                        <div className="w-10 h-10 mx-auto mb-2 rounded-lg bg-blue-100 flex items-center justify-center">
                          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <p className="text-2xl font-bold text-neutral-900">{trainerAnalytics.metrics?.totalSessions || 0}</p>
                        <p className="text-xs text-neutral-500">Total Sessions</p>
                        <p className="text-xs text-green-600 mt-1">{trainerAnalytics.metrics?.completedSessions || 0} completed</p>
                      </div>

                      <div className="bg-green-50 rounded-xl p-4 text-center">
                        <div className="w-10 h-10 mx-auto mb-2 rounded-lg bg-green-100 flex items-center justify-center">
                          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        </div>
                        <p className="text-2xl font-bold text-neutral-900">{trainerAnalytics.metrics?.totalParticipants || 0}</p>
                        <p className="text-xs text-neutral-500">Participants Trained</p>
                      </div>

                      <div className="bg-amber-50 rounded-xl p-4 text-center">
                        <div className="w-10 h-10 mx-auto mb-2 rounded-lg bg-amber-100 flex items-center justify-center">
                          <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                          </svg>
                        </div>
                        <p className="text-2xl font-bold text-neutral-900">
                          {trainerAnalytics.metrics?.avgRating > 0 ? trainerAnalytics.metrics.avgRating.toFixed(1) : 'N/A'}
                        </p>
                        <p className="text-xs text-neutral-500">Avg Rating</p>
                        <div className="flex justify-center gap-0.5 mt-1">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <svg
                              key={star}
                              className={`w-3 h-3 ${star <= Math.round(trainerAnalytics.metrics?.avgRating || 0) ? 'text-amber-400 fill-current' : 'text-neutral-300'}`}
                              viewBox="0 0 24 24"
                            >
                              <path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                            </svg>
                          ))}
                        </div>
                      </div>

                      <div className="bg-purple-50 rounded-xl p-4 text-center">
                        <div className="w-10 h-10 mx-auto mb-2 rounded-lg bg-purple-100 flex items-center justify-center">
                          <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                          </svg>
                        </div>
                        <p className="text-2xl font-bold text-neutral-900">
                          {trainerAnalytics.metrics?.avgEngagement > 0 ? `${trainerAnalytics.metrics.avgEngagement}%` : 'N/A'}
                        </p>
                        <p className="text-xs text-neutral-500">Avg Engagement</p>
                      </div>
                    </div>

                    {/* Performance Indices */}
                    <div className="bg-neutral-50 rounded-xl p-4">
                      <h3 className="text-sm font-semibold text-neutral-700 mb-2">Performance Indices</h3>
                      <p className="text-xs text-neutral-500 mb-4">Aggregated metrics across all sessions</p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {/* Trainer Effectiveness */}
                        <div className="flex flex-col items-center p-4 bg-white rounded-xl border border-neutral-200">
                          <GaugeMeter
                            value={trainerAnalytics.indices?.trainerEffectiveness || trainerAnalytics.metrics?.avgRating || 0}
                            maxValue={5}
                            label="Trainer Effectiveness"
                            band={getMetricBandInfo('tei', trainerAnalytics.indices?.trainerEffectiveness || trainerAnalytics.metrics?.avgRating || 0)?.label || 'N/A'}
                            color={getMetricBandInfo('tei', trainerAnalytics.indices?.trainerEffectiveness || trainerAnalytics.metrics?.avgRating || 0)?.color || 'gray'}
                            size="sm"
                          />
                          {(trainerAnalytics.indices?.trainerEffectiveness || trainerAnalytics.metrics?.avgRating) > 0 && (
                            <p className="mt-2 text-xs text-neutral-600 text-center leading-relaxed">
                              {getTEIInterpretation(trainerAnalytics.indices?.trainerEffectiveness || trainerAnalytics.metrics?.avgRating, 'trainer')}
                            </p>
                          )}
                        </div>

                        {/* Learning Advocacy */}
                        <div className="flex flex-col items-center p-4 bg-white rounded-xl border border-neutral-200">
                          <GaugeMeter
                            value={trainerAnalytics.indices?.learningAdvocacy || 0}
                            maxValue={5}
                            label="Learning Advocacy"
                            band={getMetricBandInfo('lai', trainerAnalytics.indices?.learningAdvocacy || 0)?.label || 'N/A'}
                            color={getMetricBandInfo('lai', trainerAnalytics.indices?.learningAdvocacy || 0)?.color || 'gray'}
                            size="sm"
                          />
                          {trainerAnalytics.indices?.learningAdvocacy > 0 && (
                            <p className="mt-2 text-xs text-neutral-600 text-center leading-relaxed">
                              {getLAIInterpretation(trainerAnalytics.indices?.learningAdvocacy, 'trainer')}
                            </p>
                          )}
                        </div>

                        {/* Total Challenges */}
                        <div className="flex flex-col items-center justify-center p-4 bg-white rounded-xl border border-neutral-200">
                          <div className="w-20 h-20 rounded-full bg-orange-100 flex items-center justify-center mb-2">
                            <span className="text-2xl font-bold text-orange-600">
                              {trainerAnalytics.indices?.totalChallenges || 0}
                            </span>
                          </div>
                          <p className="text-sm font-medium text-neutral-700">Total Challenges</p>
                          <p className="text-xs text-neutral-500">Captured in sessions</p>
                        </div>

                        {/* Action Plans */}
                        <div className="flex flex-col items-center justify-center p-4 bg-white rounded-xl border border-neutral-200">
                          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-2">
                            <span className="text-2xl font-bold text-green-600">
                              {trainerAnalytics.indices?.totalActionPlans || 0}
                            </span>
                          </div>
                          <p className="text-sm font-medium text-neutral-700">Action Plans</p>
                          <p className="text-xs text-neutral-500">Created by participants</p>
                        </div>
                      </div>
                    </div>

                    {/* Two Column Layout: Engagement Trends + Top Sessions */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {/* Engagement Trends */}
                      <div className="bg-neutral-50 rounded-xl p-4">
                        <h3 className="text-sm font-semibold text-neutral-700 mb-4">Engagement Trends</h3>
                        <p className="text-xs text-neutral-500 mb-4">Weekly engagement and participant count</p>
                        <div className="space-y-3">
                          {(trainerAnalytics.trends || []).map((trend, index) => (
                            <div key={index}>
                              <div className="flex justify-between text-sm mb-1">
                                <span className="font-medium text-neutral-700">{trend.week}</span>
                                <div className="flex gap-4">
                                  <span className="text-neutral-500 text-xs">{trend.participants} participants</span>
                                  <span className="text-primary-600 font-medium text-xs">{trend.engagement}%</span>
                                </div>
                              </div>
                              <div className="w-full bg-neutral-200 rounded-full h-2 overflow-hidden">
                                <div
                                  className="h-full bg-gradient-to-r from-primary-500 to-primary-600 rounded-full transition-all duration-500"
                                  style={{ width: `${Math.min(100, trend.engagement)}%` }}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Top Performing Sessions */}
                      <div className="bg-neutral-50 rounded-xl p-4">
                        <h3 className="text-sm font-semibold text-neutral-700 mb-4">Top Performing Sessions</h3>
                        <p className="text-xs text-neutral-500 mb-4">Sessions ranked by engagement rate</p>
                        <div className="space-y-2">
                          {(trainerAnalytics.sessionPerformance || [])
                            .sort((a, b) => (b.engagement || 0) - (a.engagement || 0))
                            .slice(0, 5)
                            .map((session, index) => (
                              <div key={session.id} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-neutral-200">
                                <div className="flex items-center justify-center w-7 h-7 bg-primary-100 text-primary-700 font-bold rounded-full text-sm flex-shrink-0">
                                  {index + 1}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-neutral-900 truncate">{session.name}</p>
                                  <p className="text-xs text-neutral-500">
                                    {session.date
                                      ? new Date(session.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
                                      : 'N/A'}
                                    {' • '}{session.participants || 0} participants
                                  </p>
                                </div>
                                <Badge
                                  variant={session.engagement >= 90 ? 'success' : session.engagement >= 75 ? 'info' : 'warning'}
                                >
                                  {session.engagement}%
                                </Badge>
                              </div>
                            ))}
                        </div>
                      </div>
                    </div>

                    {/* Additional Stats Row */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Training Hours */}
                      <div className="bg-indigo-50 rounded-xl p-4 flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-indigo-800">Total Training Hours</p>
                          <p className="text-3xl font-bold text-indigo-900 mt-1">
                            {trainerAnalytics.metrics?.totalHours?.toFixed(1) || '0.0'}
                          </p>
                          <p className="text-xs text-indigo-600 mt-1">
                            Across {trainerAnalytics.metrics?.completedSessions || 0} sessions
                          </p>
                        </div>
                        <div className="p-3 bg-indigo-100 rounded-lg">
                          <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                      </div>

                      {/* Session Success Rate */}
                      <div className="bg-green-50 rounded-xl p-4 flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-green-800">Session Success Rate</p>
                          <p className="text-3xl font-bold text-green-900 mt-1">
                            {trainerAnalytics.metrics?.completedSessions > 0 && trainerAnalytics.metrics?.totalSessions > 0
                              ? Math.round((trainerAnalytics.metrics.completedSessions / trainerAnalytics.metrics.totalSessions) * 100)
                              : 0}%
                          </p>
                          <p className="text-xs text-green-600 mt-1">
                            {trainerAnalytics.metrics?.completedSessions || 0} of {trainerAnalytics.metrics?.totalSessions || 0} completed
                          </p>
                        </div>
                        <div className="p-3 bg-green-100 rounded-lg">
                          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* Recent Participant Feedback */}
                    {trainerAnalytics.recentFeedback && trainerAnalytics.recentFeedback.length > 0 && (
                      <div className="bg-neutral-50 rounded-xl p-4">
                        <h3 className="text-sm font-semibold text-neutral-700 mb-4">Recent Participant Feedback</h3>
                        <div className="space-y-3">
                          {trainerAnalytics.recentFeedback.map((feedback) => (
                            <div key={feedback.id} className="p-4 bg-white rounded-lg border border-neutral-200">
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                                    {feedback.participantName?.charAt(0) || 'A'}
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-neutral-900">{feedback.participantName || 'Anonymous'}</p>
                                    <p className="text-xs text-neutral-500">{feedback.sessionName}</p>
                                  </div>
                                </div>
                                <div className="flex gap-0.5">
                                  {[1, 2, 3, 4, 5].map((star) => (
                                    <svg
                                      key={star}
                                      className={`w-4 h-4 ${star <= feedback.rating ? 'text-amber-400 fill-current' : 'text-neutral-300'}`}
                                      viewBox="0 0 24 24"
                                    >
                                      <path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                                    </svg>
                                  ))}
                                </div>
                              </div>
                              <p className="text-sm text-neutral-700">{feedback.comments}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Modal Footer */}
                  <div className="p-6 border-t border-neutral-200 flex items-center justify-between">
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setShowTrainerPreview(false);
                        setPreviewTrainer(null);
                        setTrainerAnalytics(null);
                      }}
                    >
                      Close
                    </Button>
                    <Button
                      variant="primary"
                      onClick={async () => {
                        try {
                          const trainerName = `${previewTrainer.firstName}-${previewTrainer.lastName}`;
                          await exportService.exportTrainerReportPDF(previewTrainer.id, trainerName);
                          toast.success('Report downloaded as PDF');
                        } catch (error) {
                          toast.error('Failed to download report');
                        }
                      }}
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Download Report
                    </Button>
                  </div>
                </>
              ) : (
                <div className="p-8 text-center">
                  <p className="text-neutral-600">Failed to load trainer data</p>
                  <Button variant="ghost" className="mt-4" onClick={() => {
                    setShowTrainerPreview(false);
                    setTrainerAnalytics(null);
                  }}>
                    Close
                  </Button>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ReportsPage;
